import {defineStore} from 'pinia';
import {ModuleApplication} from '@/stores/moduleConst';
import {BaseStore} from '@/stores/baseStore';
import applicationApi from '@/apis/platform/applicationApi';
import userApplicationApi from '@/apis/platform/userApplicationApi';
import commonFunction from '@/commons/commonFunction';
import { useContextStore } from '@/stores/contextStore';

const applicationStore = new BaseStore(applicationApi);

export const useApplicationStore = defineStore(ModuleApplication, {
  state: () => ({
    ...applicationStore.state,
    _config: {},
    moduleName: ModuleApplication,
    // Lưu trữ danh sách apps
    apps: [],
    // lưu trữ role apps
    roleApps: [],

    registerApps: [],
    
    // Cache trạng thái quyền AdminSystem theo tenantId
    hasAdminRoleByTenant: {}, // { tenantId1: true/false, tenantId2: true/false }
  }),

  getters: {
    /**
     * Lấy thông tin app theo appCode
     * @param {string} appCode
     * @returns {Object} app info
     */
    // [tbngoc - 2026-05-18] Sửa: so sánh không phân biệt hoa thường để tránh lỗi error_app_not_found
    getAppByCode: state => appCode => {
      const code = appCode?.toLowerCase();
      return state.apps.find(app => app.application_code?.toLowerCase() === code);
    },
  },

  actions: {
    ...applicationStore.action,

    /**
     * lấy dữ liệu danh sách các ứng dụng lên dashboard
     * vqphong - 24/04/2025
     * Updated: Bỏ getRoleApps() để lazy load - 19/01/2026
     */
    async getListApps() {
      try {
        commonFunction.mask();
        const result = await userApplicationApi.getUserApplications();

        // Lưu danh sách apps vào state
        if (result && result.length > 0) {
          this.apps = result;
        }
        return result;
      } catch (error) {
        console.log(error);
      } finally {
        commonFunction.unmask();
      }
    },
    
    /**
     * Lấy thông tin ứng dụng theo appCode từ store
     * @param {string} appCode
     * @returns {Object|null} app info
     */
    getAppInfo(appCode) {
      return this.getAppByCode(appCode);
    },

    updateRegisterApps(data) {
      if (data) {
        this.registerApps = data;
      }
    },

    /**
     * Kiểm tra quyền AdminSystem với cache theo tenantId
     * @param {string} tenantId - ID của tenant cần kiểm tra
     * @returns {Promise<boolean>}
     * CreatedBy: 19/01/2026
     * Updated: Cache theo tenantId - 20/01/2026
     */
    async checkAdminSystemRole(tenantId) {
      if (!tenantId) {
        const contextStore = useContextStore();
        tenantId = contextStore?.context?.Context?.TenantId;
      }

      const cacheKey = `hasAdminRole_cache_${tenantId}`;

      // 1. Check localStorage cache theo tenantId
      const cachedResult = this.getCacheWithExpiry(cacheKey);
      if (cachedResult != null) {
        return cachedResult;
      }

      // 2. Check memory cache theo tenantId
      if (this.hasAdminRoleByTenant[tenantId] != null) {
        this.setCacheWithExpiry(cacheKey, this.hasAdminRoleByTenant[tenantId]);
        return this.hasAdminRoleByTenant[tenantId];
      }

      // 3. Gọi API với tenantId
      const result = await applicationApi.hasAdminSystemRole(tenantId);
      
      // 4. Lưu vào cả memory và localStorage theo tenantId
      this.hasAdminRoleByTenant[tenantId] = result;
      this.setCacheWithExpiry(cacheKey, result);
      
      return result;
    },

    /**
     * Reset cache hasAdminSystemRole theo tenantId (dùng khi cần refresh quyền)
     * @param {string} tenantId - ID của tenant cần reset cache
     * @returns {void}
     * CreatedBy: 19/01/2026
     * Updated: Reset theo tenantId - 20/01/2026
     */
    resetAdminSystemRole(tenantId) {
      if (!tenantId) {
        console.warn('resetAdminSystemRole: tenantId is required');
        return;
      }
      
      // Xóa memory cache
      delete this.hasAdminRoleByTenant[tenantId];
      
      // Xóa localStorage cache
      const cacheKey = `hasAdminRole_cache_${tenantId}`;
      this.clearCache(cacheKey);
    },

    /**
     * Reset toàn bộ cache hasAdminSystemRole của tất cả tenant
     * @returns {void}
     * CreatedBy: 20/01/2026
     */
    resetAllAdminSystemRole() {
      // Xóa tất cả memory cache
      this.hasAdminRoleByTenant = {};
      
      // Xóa tất cả localStorage cache của admin role
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('hasAdminRole_cache_')) {
          localStorage.removeItem(key);
        }
      });
    },
    setCacheWithExpiry(key, value, expiryMinutes = 30){
      const now = new Date();
      const item = {
        value: value,
        expiry: now.getTime() + (expiryMinutes * 60 * 1000)
      };
      localStorage.setItem(key, JSON.stringify(item));
    },
    getCacheWithExpiry(key){
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return null;
      
      const item = JSON.parse(itemStr);
      const now = new Date();
      
      // Check expire
      if (now.getTime() > item.expiry) {
        localStorage.removeItem(key);
        return null;
      }
      
      return item.value;
    },
    clearCache(key){
      localStorage.removeItem(key);
    }
  },

  // Cấu hình persistence cho store
  persist: {
    storage: localStorage,
    paths: ['apps', 'roleApps', 'hasAdminRoleByTenant'], // Cache apps, roleApps và hasAdminRoleByTenant
    beforeRestore: ctx => {
      console.log('Restoring applicationStore from localStorage');
    },
    afterRestore: ctx => {
      console.log('ApplicationStore restored from localStorage');
    },
  },
});
